import React, { useEffect, useState } from 'react'
import AsyncCreatableSelect from 'react-select/async-creatable'
import debounce from 'debounce-promise'
import { Button, Card } from '@arnsbogroup/react-voicearchive'
import { useIntl } from 'react-intl'
import messages from './messages'

const getDomain = (input) => {
  try {
    if (input.includes('@')) return input.split('@').pop().toLowerCase()
    const url = input.startsWith('http') ? input : `https://${input}`
    return new URL(url).hostname.replace(/^www\./, '').toLowerCase()
  } catch {
    return input.toLowerCase()
  }
}

const isUrl = (s) => /^(https?:\/\/)?[\w-]+(\.[\w-]+)+(\/.*)?$/.test(s.trim())
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())

const loadBrandOptions = debounce((query) =>
  fetch(`/rest/v2/customer_brand${query.length ? `?search[displayName]=${encodeURIComponent(query)}` : ''}`)
    .then((r) => r.json())
    .then((brands) =>
      (Array.isArray(brands) ? brands : []).map((brand) => ({
        label: brand.displayName || brand.name,
        value: brand.uid,
        brand: { ...brand, theme: typeof brand.theme === 'string' ? JSON.parse(brand.theme || 'null') : brand.theme },
      }))
    )
    .catch(() => []),
  200
)

const MemoryRequestActions = ({ memoryIds = [], onSuccess = () => {}, defaultOpen = false, onCancel = null }) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [selectedType, setSelectedType] = useState(null)
  const [link, setLink] = useState(null)
  const [linkType, setLinkType] = useState(null)
  const [copied, setCopied] = useState(false)
  const [brandOption, setBrandOption] = useState(null)
  const [brandLoading, setBrandLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (defaultOpen && !isOpen && !link) setIsOpen(true)
  }, [isOpen, link])

  const onBrandChange = (option, meta) => {
    if (!option) { setBrandOption(null); return }
    if (meta.action === 'create-option') {
      const domain = getDomain(option.value)
      setBrandOption({ label: `Fetching brand for "${domain}"…`, value: option.value, brand: null })
      setBrandLoading(true)
      fetch(`/rest/v2/brand?domain=${encodeURIComponent(domain)}`, { method: 'POST' })
        .then((r) => r.json())
        .then(({ data }) => {
          setBrandOption(data?.uid ? { label: data.displayName || data.name, value: data.uid, brand: data } : null)
        })
        .catch(() => setBrandOption(null))
        .finally(() => setBrandLoading(false))
    } else {
      setBrandOption(option)
    }
  }

  const handleClose = () => {
    setIsOpen(false)
    setSelectedType(null)
  }

  const createRequest = async () => {
    if (!selectedType) return
    setCreating(true)
    try {
      const brandUid = brandOption?.value ? parseInt(brandOption.value, 10) || null : null
      const res = await fetch('/rest/v2/memoryrequest', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: selectedType, memoryIds, ...(brandUid ? { brandUid } : {}) }),
      }).then(r => r.json())
      if (!res.success) {
        toastr.error((res.errors || []).join(', '))
        return
      }
      setLink(res.data.href)
      setLinkType(selectedType)
      navigator.clipboard.writeText(res.data.href).then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 2500)
      })
      toastr.success(`Link copied: ${res.data.href}`)
      handleClose()
      onSuccess()
    } catch (e) {
      toastr.error(e.message)
    } finally {
      setCreating(false)
    }
  }

  const copyLink = () => {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }

  if (!link && memoryIds.length === 0) return null

  return (
    <div className="space-y-3">
      {link && (
        <Card className="p-4 space-y-2">
          <p className="text-sm font-semibold">
            {linkType === 'MemorySharing'
              ? formatMessage(messages.sharingLinkCreated)
              : formatMessage(messages.recordingRequestLinkCreated)}
          </p>
          <div className="flex gap-2 items-center">
            <input
              readOnly
              value={link}
              className="flex-1 min-w-0 border rounded px-2 py-1 text-sm font-mono bg-gray-100 outline-none truncate"
              onFocus={e => e.target.select()}
            />
            <Button size="sm" onClick={copyLink} state={copied ? 'positive' : 'primary'}>
              {copied ? formatMessage(messages.copied) : formatMessage(messages.copy)}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setLink(null)}>
              {formatMessage(messages.dismiss)}
            </Button>
          </div>
        </Card>
      )}

      {memoryIds.length > 0 && !isOpen && (
        <Button variant="outline" size="sm" onClick={() => { setSelectedType(null); setIsOpen(true) }}>
          {formatMessage(messages.requestLink)}
        </Button>
      )}

      {memoryIds.length > 0 && isOpen && (
        <Card className="p-4 space-y-4">
          <p className="text-sm font-semibold text-gray-700">
            {formatMessage(messages.selectLinkType)}
          </p>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setSelectedType('MemorySharing')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedType === 'MemorySharing'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="font-medium text-sm text-gray-800">
                {formatMessage(messages.sharingLinkTitle)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {formatMessage(messages.sharingLinkDesc)}
              </div>
            </button>

            <button
              type="button"
              onClick={() => setSelectedType('RecordingRequest')}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                selectedType === 'RecordingRequest'
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="font-medium text-sm text-gray-800">
                {formatMessage(messages.recordingRequestTitle)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {formatMessage(messages.recordingRequestDesc)}
              </div>
            </button>
          </div>

          <AsyncCreatableSelect
            isClearable
            value={brandOption}
            onChange={onBrandChange}
            isLoading={brandLoading}
            isDisabled={brandLoading}
            placeholder={formatMessage(messages.brandPickerPlaceholder)}
            formatCreateLabel={(q) => `${formatMessage(messages.brandFetchLabel)} "${getDomain(q)}"`}
            isValidNewOption={(q) => isUrl(q) || isEmail(q)}
            cacheOptions
            loadOptions={loadBrandOptions}
            defaultOptions
            styles={{
              control: (base) => ({ ...base, minHeight: 36, fontSize: 14 }),
              menu: (base) => ({ ...base, zIndex: 20 }),
            }}
          />

          <div className="flex gap-2">
            <Button
              state="primary"
              onClick={createRequest}
              disabled={!selectedType || creating || brandLoading}
            >
              {creating ? formatMessage(messages.creating) : formatMessage(messages.createLink)}
            </Button>
            <Button variant="outline" onClick={() => { handleClose(); onCancel?.() }}>
              {formatMessage(messages.cancel)}
            </Button>
          </div>
        </Card>
      )}
    </div>
  )
}

export default MemoryRequestActions
